import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { comparisonColumns } from '../../GscTools/columns';
import { reportsPosition } from '../../GscTools/metrics';
import { COMPARISON, formatPeriod } from '../../GscTools/period';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { MODE, VIEW } from '../scope';

const MODES = [MODE.POPULAR, MODE.GAINING, MODE.DECLINING];

/**
 * Block 02 — the retrieved pages or queries, with what each one did in both periods.
 *
 * The two periods were fetched independently and joined on the exact key Search Console returned,
 * so a row present in only one of them keeps that fact: its missing side reads "not returned",
 * never zero, and it takes no part in a gain or a decline. Modes re-order the rows already held
 * rather than re-asking, which is why switching one is instant and why Gaining and Declining can
 * show fewer rows than All without anything having been lost.
 *
 * Picking a row is what blocks 03 and 04 are about; only one row at a time, because both of
 * those blocks are a single row's evidence. The control is a Breakdown button at the end of each
 * row rather than a checkbox on it: a checkbox reads as "tick several, then act", which is
 * neither what this does nor what it is for, while a button says what will happen and does it on
 * one click. The pick is then stated three times over — the button reads Showing, the row stays
 * marked, and the chip beside the table names it and offers the way back out.
 */
class RowsTable extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.additionalBox = this.additionalBox.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /**
     * Clicking the row does what its button does, including toggling back off.
     *
     * The same handler rather than a second path to the same place, so the two can never drift:
     * whatever the button means by "pick this row", the row means too.
     */
    handleRowClick(item) {
        const { selectedKey, onSelect } = this.props;
        onSelect(item.key === selectedKey ? null : item.key);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    /**
     * The last column: the button that loads a row's breakdown into the blocks below.
     *
     * It is named after what it loads rather than after the act of loading it. On the pages view
     * a row opens the queries behind that page, on the queries view the pages behind that query —
     * so the column reads "Query" or "Page", which is the thing the reader is about to get. A
     * single fixed word for both would be right about neither.
     *
     * It is a toggle rather than a one-way pick, so the row that is being shown offers the way
     * back out from the same place it was chosen. The column is left out of the exports — a
     * control is not a value, and a spreadsheet column of empty cells reads as missing data.
     */
    actionColumn() {
        const { t, scope, selectedKey, onSelect } = this.props;

        const label =
            scope.view === VIEW.PAGES ? t('tools.gsc.columns.query') : t('tools.gsc.columns.page');

        return {
            label,
            key: 'details',
            data: 'key',
            // There is no order to a column of buttons, so its header leaves the rows as they
            // are rather than running the numeric default over an undefined field.
            sortType: (array) => array,
            cellSize: 'minmax(130px, 150px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === selectedKey;

                // `.table-action-main-btn` is the app's in-table action button (the Entities
                // table's Actions column is the other one), wrapped the same way so it sizes to
                // its label instead of stretching across the cell.
                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked ? t('tools.pages_queries_changes.rows.details_showing') : label}
                        </button>
                    </div>
                );
            },
        };
    }

    /**
     * Popular / Gaining / Declining, drawn as the table-filter control the Categories table uses,
     * which is what this product reaches for whenever a table is filtered to one of a fixed set
     * of views. The metric the order reads sits beside the group rather than inside it: it is not
     * one of the three views, it is what all three are measured in.
     */
    additionalBox() {
        const { t, scope, selectedKey, onMode, onSelect } = this.props;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {MODES.map((mode) => (
                            <button
                                type="button"
                                key={mode}
                                className={scope.mode === mode ? 'active' : ''}
                                aria-pressed={scope.mode === mode}
                                onClick={() => onMode(mode)}
                            >
                                {t(`tools.pages_queries_changes.modes.${mode}`)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="gsc-tool-rows-meta">
                    <span className="gsc-tool-sub">
                        {t('tools.pages_queries_changes.rows.ordered_by', {
                            metric: t(`tools.gsc.metrics.${scope.metric}`),
                        })}
                    </span>
                    {!!selectedKey && (
                        <span className="gsc-tool-chip">
                            <span>
                                {t('tools.pages_queries_changes.rows.selected')}: {selectedKey}
                            </span>
                            <button
                                type="button"
                                aria-label={t('tools.pages_queries_changes.rows.clear_selection')}
                                onClick={() => onSelect(null)}
                            >
                                <i className="icon-x-close" />
                            </button>
                        </span>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const {
            t,
            scope,
            status,
            error,
            rows,
            hiddenByPreset,
            selectedKey,
            stopReason,
            loadingMore,
            onLoadMore,
        } = this.props;

        const isPages = scope.view === VIEW.PAGES;
        const columns = comparisonColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            keyLabel: isPages ? t('tools.gsc.columns.page') : t('tools.gsc.columns.query'),
            isUrl: isPages,
        });

        // The action column is the table's, not the report's: it holds a control rather than a
        // figure, so the exports are given the columns without it.
        const tableColumns = [...columns, this.actionColumn()];

        const noComparison = scope.comparison === COMPARISON.NONE || !scope.baseline;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">{t('tools.pages_queries_changes.rows.title')}</p>
                        <p className="gsc-tool-sub">
                            {t('tools.pages_queries_changes.rows.description')} ·{' '}
                            {formatPeriod(scope.current)}
                            {!noComparison && <> ↔ {formatPeriod(scope.baseline)}</>}
                        </p>
                        {/* The consequence of picking a row belongs here, where the rows are,
                            rather than only in the two blocks it feeds — which sit below the
                            fold and so are read after the choice, not before it. */}
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {isPages
                                ? t('tools.pages_queries_changes.rows.select_hint_queries')
                                : t('tools.pages_queries_changes.rows.select_hint_pages')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {noComparison && (
                        <div className="gsc-tool-banner">
                            {t('tools.pages_queries_changes.rows.no_comparison')}
                        </div>
                    )}

                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            {!!error && <div className="gsc-tool-banner error">{error}</div>}

                            {!!hiddenByPreset && (
                                <div className="gsc-tool-banner">
                                    {t('tools.pages_queries_changes.rows.preset_hidden', {
                                        count: hiddenByPreset,
                                    })}
                                </div>
                            )}

                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                additionalBox={this.additionalBox}
                                fileName={t('tools.pages_queries_changes.rows.title')}
                                customEmptyMessage={t(
                                    `tools.pages_queries_changes.rows.empty.${scope.mode}`,
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                // The row whose breakdown is on screen stays marked while the
                                // blocks it fills are read further down the page.
                                rowClassName={(item) =>
                                    item.key === selectedKey ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t(`tools.gsc.stop.${stopReason}`)}{' '}
                                {t('tools.pages_queries_changes.rows.subset')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(RowsTable);
